import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { StaffAttendanceRecord } from '@/mocks/staffAttendance';

interface StaffAttendanceTableProps {
  records: StaffAttendanceRecord[];
  selectedDate: string;
  onToggle: (recordId: string) => void;
  onRemarkChange: (recordId: string, remark: string) => void;
  onMarkAll: (status: 'present' | 'absent') => void;
  searchQuery: string;
  roleFilter: string;
}

const todayStr = new Date().toISOString().split('T')[0];

export default function StaffAttendanceTable({
  records,
  selectedDate,
  onToggle,
  onRemarkChange,
  onMarkAll,
  searchQuery,
  roleFilter,
}: StaffAttendanceTableProps) {
  const [editingRemarkId, setEditingRemarkId] = useState<string | null>(null);
  const [remarkDraft, setRemarkDraft] = useState('');
  const [confirmAction, setConfirmAction] = useState<'present' | 'absent' | null>(null);

  const isEditable = selectedDate === todayStr;

  const filtered = useMemo(() => {
    let result = [...records];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (r) =>
          r.staffName.toLowerCase().includes(q) ||
          r.staffId.toLowerCase().includes(q) ||
          r.role.toLowerCase().includes(q),
      );
    }
    if (roleFilter) {
      result = result.filter((r) => r.role === roleFilter);
    }
    return result;
  }, [records, searchQuery, roleFilter]);

  const presentCount = filtered.filter((r) => r.status === 'present').length;
  const absentCount = filtered.filter((r) => r.status === 'absent').length;

  const statusLabel = (status: string) => {
    switch (status) {
      case 'present': return 'Present';
      case 'absent': return 'Absent';
      case 'on-leave': return 'On Leave';
      case 'half-day': return 'Half-Day';
      default: return status;
    }
  };

  const statusBadgeClass = (status: string) => {
    switch (status) {
      case 'present': return 'bg-semantic-success-bg text-foreground-800 border-semantic-success/30';
      case 'absent': return 'bg-semantic-error-bg text-foreground-800 border-semantic-error/30';
      case 'on-leave': return 'bg-secondary-100 text-foreground-800 border-secondary-200';
      case 'half-day': return 'bg-semantic-warning-bg text-foreground-800 border-semantic-warning/40';
      default: return 'bg-background-100 text-foreground-600 border-background-200';
    }
  };

  const statusDotClass = (status: string) => {
    switch (status) {
      case 'present': return 'bg-semantic-success';
      case 'absent': return 'bg-semantic-error';
      case 'on-leave': return 'bg-secondary-500';
      case 'half-day': return 'bg-semantic-warning';
      default: return 'bg-foreground-400';
    }
  };

  const startEditingRemark = (record: StaffAttendanceRecord) => {
    setEditingRemarkId(record.id);
    setRemarkDraft(record.remark);
  };

  const saveRemark = (recordId: string) => {
    onRemarkChange(recordId, remarkDraft);
    setEditingRemarkId(null);
    setRemarkDraft('');
  };

  const cancelRemark = () => {
    setEditingRemarkId(null);
    setRemarkDraft('');
  };

  const renderRow = (record: StaffAttendanceRecord) => (
    <tr key={record.id} className="hover:bg-background-50/60 transition-colors">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-xs font-heading font-700 flex-shrink-0">
            {record.staffName.split(' ').map((n) => n[0]).slice(0, 2).join('')}
          </div>
          <div>
            <p className="text-sm font-medium text-foreground-800 font-label">{record.staffName}</p>
            <p className="text-xs text-foreground-400 font-mono">
              {record.role} &middot; {record.staffId}
            </p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="text-xs text-foreground-600 font-label">{record.className}</span>
      </td>
      <td className="px-4 py-3">
        <span className="text-xs text-foreground-400 font-mono">
          {record.checkIn || '—'}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="text-xs text-foreground-400 font-mono">
          {record.checkOut || '—'}
        </span>
      </td>
      <td className="px-4 py-3">
        {isEditable && record.status !== 'on-leave' ? (
          <button
            onClick={() => onToggle(record.id)}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap ${
              record.status === 'present' || record.status === 'half-day'
                ? 'bg-semantic-success'
                : 'bg-semantic-error'
            }`}
            title={`${statusLabel(record.status)} — click to toggle`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-background-50 transition-transform duration-200 ${
                record.status === 'present' || record.status === 'half-day' ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        ) : (
          <span
            className={`inline-flex items-center gap-1.5 text-xs font-label font-medium px-2.5 py-1 rounded-full border ${statusBadgeClass(record.status)}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${statusDotClass(record.status)}`} />
            {statusLabel(record.status)}
          </span>
        )}
      </td>
      <td className="px-4 py-3 max-w-[200px]">
        {editingRemarkId === record.id ? (
          <div className="flex items-center gap-1">
            <input
              type="text"
              value={remarkDraft}
              onChange={(e) => setRemarkDraft(e.target.value)}
              placeholder="Add remark..."
              className="flex-1 px-2 py-1 text-xs font-label rounded-md border border-secondary-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-secondary-300 focus:border-secondary-400"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveRemark(record.id);
                if (e.key === 'Escape') cancelRemark();
              }}
            />
            <button onClick={() => saveRemark(record.id)} className="w-6 h-6 flex items-center justify-center rounded-md bg-semantic-success text-background-50 cursor-pointer whitespace-nowrap" title="Save">
              <i className="ri-check-line text-xs" />
            </button>
            <button onClick={cancelRemark} className="w-6 h-6 flex items-center justify-center rounded-md bg-background-100 text-foreground-500 hover:text-foreground-700 cursor-pointer whitespace-nowrap" title="Cancel">
              <i className="ri-close-line text-xs" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 group">
            {record.remark ? (
              <span className="text-xs text-foreground-600 font-label truncate block">{record.remark}</span>
            ) : (
              <span className="text-xs text-foreground-300 font-label italic">No remark</span>
            )}
            {isEditable && (
              <button
                onClick={() => startEditingRemark(record)}
                className="w-5 h-5 flex items-center justify-center rounded text-foreground-400 hover:text-accent-600 hover:bg-accent-50 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                title="Edit remark"
              >
                <i className="ri-edit-line text-xs" />
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );

  const renderMobileCard = (record: StaffAttendanceRecord) => {
    const initials = record.staffName.split(' ').slice(0, 2).map((n) => n[0]).join('');
    return (
      <div
        key={record.id}
        className={`p-3 rounded-xl border ${
          record.status === 'present' ? 'border-semantic-success/20 bg-semantic-success-bg/30'
          : record.status === 'absent' ? 'border-semantic-error/20 bg-semantic-error-bg/30'
          : record.status === 'half-day' ? 'border-semantic-warning/20 bg-semantic-warning-bg/30'
          : 'border-background-200 bg-background-50'
        }`}
      >
        {/* Top row: avatar + name + role */}
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-xs font-heading font-700 flex-shrink-0">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-label font-medium text-foreground-800 truncate">{record.staffName}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-foreground-400 font-mono">{record.staffId}</span>
              <span className="text-[10px] bg-accent-100 text-accent-700 px-1.5 py-0.5 rounded-full font-label whitespace-nowrap">
                {record.role}
              </span>
            </div>
          </div>
          {!isEditable && (
            <span className={`inline-flex items-center gap-1 text-xs font-label font-medium px-2 py-1 rounded-full border ${statusBadgeClass(record.status)}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${statusDotClass(record.status)}`} />
              {statusLabel(record.status)}
            </span>
          )}
        </div>

        {/* Check-in / Check-out times */}
        <div className="flex items-center gap-4 mb-2 text-xs text-foreground-500 font-label">
          <span className="flex items-center gap-1">
            <i className="ri-login-box-line text-foreground-400" />
            In: {record.checkIn || '—'}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-logout-box-line text-foreground-400" />
            Out: {record.checkOut || '—'}
          </span>
          <span className="text-foreground-300">— {record.className}</span>
        </div>

        {/* Full-width attendance toggle */}
        {isEditable && record.status !== 'on-leave' ? (
          <div className="mb-2">
            <button
              onClick={() => onToggle(record.id)}
              className={`w-full flex items-center justify-center gap-2 h-10 rounded-lg text-sm font-label font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                record.status === 'present' || record.status === 'half-day'
                  ? 'bg-semantic-success text-background-50 shadow-[0_0_0_2px_rgba(34,197,94,0.3)]'
                  : 'bg-semantic-error text-background-50 shadow-[0_0_0_2px_rgba(239,68,68,0.3)]'
              }`}
            >
              <i className={`text-base ${record.status === 'present' || record.status === 'half-day' ? 'ri-check-line' : 'ri-close-line'}`} />
              {statusLabel(record.status)}
              <span className="text-xs opacity-80 font-normal">— tap to toggle</span>
            </button>
          </div>
        ) : record.status === 'on-leave' ? (
          <div className="mb-2">
            <span className="w-full flex items-center justify-center gap-2 h-10 rounded-lg text-sm font-label font-semibold bg-secondary-100 text-foreground-800 border border-secondary-200">
              <i className="ri-calendar-event-line text-base" />
              On Leave
            </span>
          </div>
        ) : null}

        {/* Remark on its own line */}
        <div>
          {editingRemarkId === record.id ? (
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={remarkDraft}
                onChange={(e) => setRemarkDraft(e.target.value)}
                placeholder="Remark..."
                className="flex-1 px-2 py-1.5 text-xs font-label rounded-md border border-secondary-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-secondary-300 focus:border-secondary-400"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveRemark(record.id);
                  if (e.key === 'Escape') cancelRemark();
                }}
              />
              <button onClick={() => saveRemark(record.id)} className="w-7 h-7 flex items-center justify-center rounded-md bg-semantic-success text-background-50 cursor-pointer flex-shrink-0 whitespace-nowrap">
                <i className="ri-check-line text-xs" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => isEditable && startEditingRemark(record)}
              className={`w-full text-left px-2 py-1.5 rounded-md text-xs font-label transition-colors ${
                record.remark ? 'text-foreground-600 bg-background-100/70' : 'text-foreground-300 italic'
              } ${isEditable ? 'cursor-pointer hover:bg-background-200/70' : 'cursor-default'}`}
            >
              {record.remark || 'No remark'}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden">
      {/* Summary + Bulk Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between px-5 py-3 border-b border-background-200 gap-3">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-semantic-success" />
            <span className="text-sm font-label text-foreground-700">
              <span className="font-700">{presentCount}</span> Present
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-semantic-error" />
            <span className="text-sm font-label text-foreground-700">
              <span className="font-700">{absentCount}</span> Absent
            </span>
          </div>
        </div>
        {isEditable && filtered.length > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setConfirmAction('present')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-label font-medium rounded-lg bg-semantic-success-bg text-foreground-800 border border-semantic-success/30 hover:bg-semantic-success/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-check-double-line text-sm" />
              Mark All Present
            </button>
            <button
              onClick={() => setConfirmAction('absent')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-label font-medium rounded-lg bg-semantic-error-bg text-foreground-800 border border-semantic-error/30 hover:bg-semantic-error/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-forbid-line text-sm" />
              Mark All Absent
            </button>
          </div>
        )}
      </div>

      {/* Desktop Table */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full" role="table">
          <thead>
            <tr className="border-b border-background-100">
              <th scope="col" className="text-left px-4 py-3 text-xs font-label font-600 text-foreground-500 uppercase tracking-wide">Staff</th>
              <th scope="col" className="text-left px-4 py-3 text-xs font-label font-600 text-foreground-500 uppercase tracking-wide">Class</th>
              <th scope="col" className="text-left px-4 py-3 text-xs font-label font-600 text-foreground-500 uppercase tracking-wide">Check-In</th>
              <th scope="col" className="text-left px-4 py-3 text-xs font-label font-600 text-foreground-500 uppercase tracking-wide">Check-Out</th>
              <th scope="col" className="text-left px-4 py-3 text-xs font-label font-600 text-foreground-500 uppercase tracking-wide">Status</th>
              <th scope="col" className="text-left px-4 py-3 text-xs font-label font-600 text-foreground-500 uppercase tracking-wide">Remark</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-background-100">
            {filtered.map(renderRow)}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="sm:hidden px-3 py-3 flex flex-col gap-2">
        {filtered.map(renderMobileCard)}
      </div>

      {filtered.length === 0 && (
        <div className="px-5 py-16 text-center">
          <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-background-100 mb-4">
            <i className="ri-search-line text-foreground-300 text-2xl" />
          </div>
          <p className="text-sm font-medium text-foreground-500 font-label">No staff attendance records found</p>
          <p className="text-xs text-foreground-400 font-label mt-1">Try adjusting your search or filter</p>
        </div>
      )}

      {/* Footer */}
      <div className="px-5 py-3 border-t border-background-200 flex items-center justify-between">
        <p className="text-xs text-foreground-400 font-label">{filtered.length} staff shown</p>
        {!isEditable && (
          <p className="text-xs text-foreground-400 font-label italic flex items-center gap-1">
            <i className="ri-lock-line text-xs" />
            Read-only — historical record
          </p>
        )}
        {isEditable && (
          <p className="text-xs text-foreground-400 font-label italic flex items-center gap-1">
            <i className="ri-information-line text-xs" />
            Toggle to mark attendance
          </p>
        )}
      </div>

      {/* Confirmation dialog for Mark All */}
      {confirmAction !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-foreground-950/40" onClick={() => setConfirmAction(null)} />
          <div className="relative bg-background-50 rounded-xl p-6 max-w-sm w-full mx-4 shadow-lg border border-background-200 z-10">
            <h3 className="font-heading font-700 text-foreground-800 text-base mb-2">
              {confirmAction === 'present' ? 'Mark All Present?' : 'Mark All Absent?'}
            </h3>
            <p className="text-sm text-foreground-600 font-label mb-5">
              {confirmAction === 'present'
                ? `This will mark all ${filtered.length} staff members as present for today. Are you sure?`
                : `This will mark all ${filtered.length} staff members as absent for today. Are you sure?`}
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setConfirmAction(null)}
                className="px-4 py-2 text-sm font-label font-medium text-foreground-600 hover:text-foreground-800 bg-background-100 hover:bg-background-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onMarkAll(confirmAction!);
                  setConfirmAction(null);
                }}
                className={`px-4 py-2 text-sm font-label font-medium rounded-lg cursor-pointer whitespace-nowrap ${
                  confirmAction === 'absent'
                    ? 'bg-semantic-error text-background-50 hover:bg-semantic-error/90'
                    : 'bg-semantic-success text-background-50 hover:bg-semantic-success/90'
                }`}
              >
                {confirmAction === 'present' ? 'Mark All Present' : 'Mark All Absent'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}